// components/DailyLogViewEdit.js
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { TrashIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import Select from "react-select";
import AlertDialog from "./AlertDialog";

function formatLogDate(dateInput, pattern = "MMM dd, yyyy") {
  const d = new Date(dateInput);
  const utcAsLocal = new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
  );
  return format(utcAsLocal, pattern);
}

export default function DailyLogViewEdit() {
  const { logId } = useParams();
  const navigate = useNavigate();
  const [dailyLog, setDailyLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    logDate: "",
    siteConditions: "clear",
    siteConditionsDescription: "",
    crew: [],
    workCompletedDescription: "",
    media: [],
  });
  const [crewOptions, setCrewOptions] = useState([]);
  const [deleteAlert, setDeleteAlert] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [successAlert, setSuccessAlert] = useState({
    open: false,
    title: "",
    message: "",
  });
  const [selectedIndex, setSelectedIndex] = useState(null); // null = closed

  // Custom styles for react-select
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "#1F2937",
      borderColor: "#4B5563",
      color: "#E5E7EB",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#F97316",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "#1F2937",
      color: "#E5E7EB",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? "#F97316" : "#1F2937",
      color: "#E5E7EB",
      "&:hover": {
        backgroundColor: "#374151",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#374151",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#E5E7EB",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#E5E7EB",
      "&:hover": {
        backgroundColor: "#EF4444",
        color: "#FFFFFF",
      },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: "#9CA3AF",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#E5E7EB",
    }),
  };

  // Fetch daily log and crew options
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem("token");

        // Fetch daily log
        const logResponse = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/dailyLogs/single/${logId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const log = logResponse.data.dailyLog;
        setDailyLog(log);
        setFormData({
          logDate: formatLogDate(log.logDate, "yyyy-MM-dd"),
          siteConditions: log.siteConditions,
          siteConditionsDescription: log.siteConditionsDescription || "",
          crew: log.crew.map((c) => c._id),
          workCompletedDescription: log.workCompletedDescription || "",
          media: [],
        });

        // Fetch crew options
        const usersResponse = await axios.get(
          `https://erp-production-72da01c8e651.herokuapp.com/api/users`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCrewOptions(
          usersResponse.data.map((user) => ({
            value: user._id,
            label: user.displayName,
          })),
        );

        setLoading(false);
      } catch (err) {
        setError(
          "Failed to load daily log: " +
            (err.response?.data?.message || err.message),
        );
        setLoading(false);
      }
    };
    fetchData();
  }, [logId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCrewChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      crew: selectedOptions
        ? selectedOptions.map((option) => option.value)
        : [],
    }));
  };

  const handleMediaChange = (e) => {
    setFormData((prev) => ({ ...prev, media: e.target.files }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const formDataToSend = new FormData();
      formDataToSend.append("logDate", formData.logDate);
      formDataToSend.append("siteConditions", formData.siteConditions);
      formDataToSend.append(
        "siteConditionsDescription",
        formData.siteConditionsDescription,
      );
      formDataToSend.append("crew", JSON.stringify(formData.crew));
      formDataToSend.append(
        "workCompletedDescription",
        formData.workCompletedDescription,
      );
      for (let i = 0; i < formData.media.length; i++) {
        formDataToSend.append("media", formData.media[i]);
      }

      const response = await axios.put(
        `https://erp-production-72da01c8e651.herokuapp.com/api/dailyLogs/${logId}`,
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setDailyLog(response.data.dailyLog);
      setSuccessAlert({
        open: true,
        title: "Success",
        message: "Daily log updated successfully!",
      });
    } catch (err) {
      setError(
        "Failed to update daily log: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const handleDeleteConfirm = () => {
    setDeleteAlert({
      open: true,
      title: "Confirm Delete",
      message:
        "Are you sure you want to delete this daily log? This action cannot be undone.",
    });
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `https://erp-production-72da01c8e651.herokuapp.com/api/dailyLogs/${logId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      navigate(-1); // Navigate back to previous page
    } catch (err) {
      setError(
        "Failed to delete daily log: " +
          (err.response?.data?.message || err.message),
      );
    }
  };

  const openMediaModal = (index) => {
    setSelectedIndex(index);
  };

  const closeMediaModal = () => {
    setSelectedIndex(null);
  };

  if (loading)
    return <div className="text-center py-4 text-gray-200">Loading...</div>;
  if (error)
    return <div className="text-red-600 text-center py-4">{error}</div>;

  return (
    <div className="bg-gray-200 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-gray-200"
          >
            <h2 className="text-lg font-semibold text-white bg-gray-700 rounded-md px-4 py-2">
              Back
            </h2>
          </button>
          <h2 className="text-lg font-semibold text-white bg-gray-700 rounded-md px-4 py-2">
            Daily Log - {formatLogDate(dailyLog.logDate)}
          </h2>
        </div>
      </div>

      {/* Weather Information */}
      {dailyLog.weather && (
        <div className="mb-6 bg-gray-800 rounded-lg p-4 shadow-md">
          <h3 className="text-md font-medium text-gray-200 mb-3">
            Weather Conditions
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-400">Temperature</p>
              <p className="text-lg text-gray-200">
                {dailyLog.weather.temperature}°C
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-400">Condition</p>
              <p className="text-lg text-gray-200">
                {dailyLog.weather.condition}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-400">Humidity</p>
              <p className="text-lg text-gray-200">
                {dailyLog.weather.humidity}%
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-400">Wind Speed</p>
              <p className="text-lg text-gray-200">
                {dailyLog.weather.windSpeed} km/h
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form */}
      <form
        onSubmit={handleSubmit}
        className="mb-6 bg-gray-700 p-6 rounded-lg shadow-md"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Log Date
            </label>
            <input
              type="date"
              name="logDate"
              value={formData.logDate}
              onChange={handleInputChange}
              className="block w-full rounded-md bg-gray-800 text-gray-200 border-gray-600 focus:border-orange-400 focus:ring-orange-400 focus:ring-2 focus:outline-none p-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Site Conditions
            </label>
            <select
              name="siteConditions"
              value={formData.siteConditions}
              onChange={handleInputChange}
              className="block w-full rounded-md bg-gray-800 text-gray-200 border-gray-600 focus:border-orange-400 focus:ring-orange-400 focus:ring-2 focus:outline-none p-2"
              required
            >
              <option value="clear">Clear</option>
              <option value="muddy">Muddy</option>
              <option value="dusty">Dusty</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Site Conditions Description
            </label>
            <textarea
              name="siteConditionsDescription"
              value={formData.siteConditionsDescription}
              onChange={handleInputChange}
              className="block w-full rounded-md bg-gray-800 text-gray-200 border-gray-600 focus:border-orange-400 focus:ring-orange-400 focus:ring-2 focus:outline-none p-2"
              rows="3"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Crew on Site
            </label>
            <Select
              isMulti
              options={crewOptions}
              value={crewOptions.filter((option) =>
                formData.crew.includes(option.value),
              )}
              onChange={handleCrewChange}
              styles={selectStyles}
              placeholder="Select crew members..."
              className="text-gray-200"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Work Completed Description
            </label>
            <textarea
              name="workCompletedDescription"
              value={formData.workCompletedDescription}
              onChange={handleInputChange}
              className="block w-full rounded-md bg-gray-800 text-gray-200 border-gray-600 focus:border-orange-400 focus:ring-orange-400 focus:ring-2 focus:outline-none p-2"
              rows="5"
              required
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-200 mb-1">
              Upload Photos/Videos
            </label>
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleMediaChange}
              className="block w-full text-gray-200 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-orange-400 file:text-white file:hover:bg-orange-500"
            />
          </div>
          {dailyLog.media && dailyLog.media.length > 0 && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Existing Media
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-1">
                {dailyLog.media.map((media, index) => (
                  <div key={index} className="relative">
                    <button
                      type="button"
                      onClick={() => openMediaModal(index)} // ← pass index instead of media object
                      className="focus:outline-none w-full"
                      aria-label={`View media ${index + 1}`}
                    >
                      {media.type === "video" ? (
                        <video
                          src={media.url}
                          className="w-full h-20 object-cover rounded-md hover:opacity-80 transition-opacity"
                          muted
                          loading="lazy"
                        />
                      ) : (
                        <img
                          src={media.url}
                          alt={`Media ${index}`}
                          className="w-full h-20 object-cover rounded-md hover:opacity-80 transition-opacity"
                          loading="lazy"
                        />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="bg-gray-600 text-gray-200 px-6 py-2 rounded-md hover:bg-gray-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-orange-400 text-white px-6 py-2 rounded-md hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2 focus:ring-offset-gray-700 transition-colors"
          >
            Update Daily Log
          </button>
        </div>
      </form>

      {/* Media Modal */}
      <Dialog
        open={selectedIndex !== null}
        onClose={closeMediaModal}
        className="relative z-[20000]"
      >
        <DialogBackdrop className="fixed inset-0 bg-gray-900/90 transition-opacity" />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="relative w-full max-w-5xl bg-gray-900 rounded-xl shadow-2xl overflow-hidden">
              {/* Close button */}
              <button
                type="button"
                onClick={closeMediaModal}
                className="absolute top-4 right-4 z-10 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-orange-500 rounded-full p-1"
              >
                <span className="sr-only">Close</span>
                <svg
                  className="h-8 w-8"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>

              {/* Navigation arrows - only show if more than 1 media */}
              {dailyLog.media.length > 1 && (
                <>
                  {/* Previous */}
                  <button
                    onClick={() =>
                      setSelectedIndex((prev) =>
                        prev === 0 ? dailyLog.media.length - 1 : prev - 1,
                      )
                    }
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-3 transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                    aria-label="Previous media"
                  >
                    <svg
                      className="h-10 w-10"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>

                  {/* Next */}
                  <button
                    onClick={() =>
                      setSelectedIndex((prev) =>
                        prev === dailyLog.media.length - 1 ? 0 : prev + 1,
                      )
                    }
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white/70 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-3 transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
                    aria-label="Next media"
                  >
                    <svg
                      className="h-10 w-10"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </>
              )}

              {/* Current media display */}
              {selectedIndex !== null && (
                <div className="flex items-center justify-center min-h-[70vh] max-h-[85vh] p-4">
                  {dailyLog.media[selectedIndex].type === "video" ? (
                    <video
                      src={dailyLog.media[selectedIndex].url}
                      controls
                      autoPlay
                      className="max-w-full max-h-[80vh] object-contain rounded-lg"
                    />
                  ) : (
                    <img
                      src={dailyLog.media[selectedIndex].url}
                      alt={`Media ${selectedIndex + 1}`}
                      className="max-w-full max-h-[80vh] object-contain rounded-lg"
                    />
                  )}
                </div>
              )}

              {/* Optional: show current / total counter */}
              {dailyLog.media.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 bg-black/50 px-4 py-1 rounded-full text-sm">
                  {selectedIndex + 1} / {dailyLog.media.length}
                </div>
              )}
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <AlertDialog
        open={deleteAlert.open}
        type="warning"
        title={deleteAlert.title}
        message={deleteAlert.message}
        onClose={() => setDeleteAlert({ open: false, title: "", message: "" })}
        onConfirm={handleDelete}
        showCancel={true}
      />
      <AlertDialog
        open={successAlert.open}
        type="success"
        title={successAlert.title}
        message={successAlert.message}
        onClose={() => setSuccessAlert({ open: false, title: "", message: "" })}
        onConfirm={() =>
          setSuccessAlert({ open: false, title: "", message: "" })
        }
        confirmText="OK"
        showCancel={false}
      />
    </div>
  );
}
